import { Box, Button, Container, FormHelperText, IconButton, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import CancelIcon from '@mui/icons-material/Cancel';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useFileUpload } from '../shared/hooks/use-file-upload';

type PfdFormFileUploadFieldProps = {
  name: `${string}` | `${string}.${string}` | `${string}.${number}`;
  accept: string;
  isMultiple?: boolean;
  rules?: Object;
};

export function PfdFormFileUploadField({ name, accept, isMultiple, rules }: PfdFormFileUploadFieldProps) {
  const {
    handleRemoveFile,
    handleChange,
    files,
    handleDragOver,
    handleDrop,
    handleDragEnter,
    trigger,
    clear,
    inputRef,
  } = useFileUpload();
  const {
    setError,
    formState: { isSubmitting, isDirty },
  } = useFormContext();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    rules,
  });

  const hasFiles = !!files?.length;

  const { onChange: onChangeField } = field;

  useEffect(() => {
    onChangeField(files);
  }, [files, onChangeField]);

  useEffect(() => {
    if (!field?.value?.length || field?.value?.length === 0) {
      clear();
    }
  }, [field?.value?.length, clear]);

  useEffect(() => {
    if (hasFiles) {
      setError('files', { message: 'Pdf File is required' });
    }
  }, [hasFiles, setError]);

  return (
    <>
      <input id={name} ref={inputRef} hidden type={'file'} accept={accept} onChange={handleChange} />
      <Container sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Box>
          {(!hasFiles || isMultiple) && (
            <div onDragOver={handleDragOver} onDrop={handleDrop} onDragEnter={handleDragEnter}>
              <Button onClick={trigger} startIcon={<FileUploadIcon />}>
                {`Select File${isMultiple ? '(s)' : ''}`}
              </Button>
              {error && <FormHelperText error={!!error}>{error?.message}</FormHelperText>}
            </div>
          )}
          {!!files && hasFiles && (
            <>
              {files.map(file => (
                <Box display='flex' alignItems='center' key={file.name}>
                  <Box>
                    <Typography>{file.name}</Typography>
                  </Box>
                  <Box sx={{ paddingLeft: 1 }}>
                    <IconButton title='Remove file' onClick={() => handleRemoveFile(file)}>
                      <CancelIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
        <Button
          type='submit'
          variant='contained'
          color='secondary'
          startIcon={<FileUploadIcon />}
          disabled={isSubmitting || !isDirty}
        >
          Upload
        </Button>
      </Container>
    </>
  );
}
